"use client";

import { updateLocale } from "@i18n/lib/update-locale";
import { useLocalePathname, useLocaleRouter } from "@i18n/routing";
import { config } from "@repo/config";
import type { Locale } from "@repo/i18n";
import { isLocaleAvailableForPath } from "@shared/lib/seo";
import { Button } from "@ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { LanguagesIcon } from "lucide-react";
import { useLocale } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ComponentProps, type ReactNode, Suspense, useState } from "react";

const { locales, defaultLocale } = config.i18n;

// 这个组件专门处理访问 searchParams
function LocaleSwitchWithParams({
	withLocaleInUrl = true,
}: {
	withLocaleInUrl?: boolean;
}) {
	const localeRouter = useLocaleRouter();
	const localePathname = useLocalePathname();
	const fullPathname = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();
	const currentLocale = useLocale();
	const [value, setValue] = useState<string>(currentLocale);

	const getPathWithoutLocale = () => {
		// 处理根语言路径的特殊情况 (如 /zh, /en)
		if (fullPathname === `/${currentLocale}`) {
			return "";
		}
		// 处理带子路径的情况 (如 /zh/blog, /en/docs)
		if (fullPathname.startsWith(`/${currentLocale}/`)) {
			return fullPathname.substring(currentLocale.length + 2);
		}
		return fullPathname.startsWith("/")
			? fullPathname.substring(1)
			: fullPathname;
	};

	const handleLocaleChange = (newLocale: string) => {
		setValue(newLocale);

		if (withLocaleInUrl) {
			const params = searchParams.toString()
				? `?${searchParams.toString()}`
				: "";

			// 直接通过构建URL并使用同步导航来避免异步问题
			const pathWithoutLocale = getPathWithoutLocale();
			const newPath = pathWithoutLocale
				? `/${newLocale}/${pathWithoutLocale}${params}`
				: `/${newLocale}${params}`;

			// 直接跳转到新URL，同步操作不会被滚动中断
			window.location.href = newPath;
		} else {
			// 设置cookie确保语言持久化
			document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000`;
			updateLocale(newLocale as Locale);

			// 直接刷新页面
			window.location.reload();
		}
	};

	// 部分翻译的语言不该出现在还没翻译的页面上——切过去只能看到英文兜底内容，
	// /docs 这类根本没有对应内容的路由甚至直接 404。当前语言永远保留，否则
	// value 找不到对应项，控件会变成空白。
	const pathWithoutLocale = getPathWithoutLocale();
	const pagePath = pathWithoutLocale ? `/${pathWithoutLocale}` : "";
	const availableLocales = Object.entries(locales).filter(
		([locale]) =>
			locale === currentLocale ||
			isLocaleAvailableForPath(locale, pagePath),
	);

	return (
		<DropdownMenuRadioGroup
			value={value}
			onValueChange={handleLocaleChange}
		>
			{availableLocales.map(([locale, { label }]) => {
				return (
					<DropdownMenuRadioItem key={locale} value={locale}>
						{label}
					</DropdownMenuRadioItem>
				);
			})}
		</DropdownMenuRadioGroup>
	);
}

// 导出的主组件使用 Suspense 包装参数组件
export function LocaleSwitch({
	withLocaleInUrl = true,
	trigger,
	contentProps,
}: {
	withLocaleInUrl?: boolean;
	/** 替换默认的图标按钮（工作台侧栏要带当前语言名的整行） */
	trigger?: ReactNode;
	contentProps?: ComponentProps<typeof DropdownMenuContent>;
}) {
	return (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>
				{trigger ?? (
					<Button variant="ghost" size="icon" aria-label="Language">
						<LanguagesIcon className="size-4" />
					</Button>
				)}
			</DropdownMenuTrigger>

			<DropdownMenuContent {...contentProps}>
				<Suspense
					fallback={
						<div className="px-2 py-1.5 text-sm">Loading...</div>
					}
				>
					<LocaleSwitchWithParams withLocaleInUrl={withLocaleInUrl} />
				</Suspense>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
